import axios from 'axios';
import Config from 'Config';
import { createSignedPostRequest } from './createSignedRequestsHelpers';

/**
 * Reports uploads that failed on the device.
 *
 * An upload is three client-driven steps — ask for a presigned URL, PUT the
 * bytes, ask the API to write the FilePermission row — and until now a failure
 * in any of them went to `console.error` on someone's phone and died there. A
 * report of a vanished photo could only be investigated by reasoning from
 * absence: no S3 object, no row, no record that an attempt was ever made.
 *
 * Nothing here is allowed to throw. Every entry point is called from inside a
 * failure handler, so a reporting bug must never become the error the user
 * sees, and must never take down the upload path that is already failing.
 */

const API_PATH = 'files/upload-diagnostics';

const QUEUE_KEY = 'uploadDiag:queue';
const PENDING_CAPTURE_KEY = 'uploadDiag:pendingCapture';

/**
 * The queue holds reports we could not send. It is a diagnostic, not data
 * anyone is waiting for, so it is capped hard and the oldest are dropped: a
 * device that has been offline for a week should not carry a week of reports,
 * and localStorage is shared with everything else in the app.
 */
const MAX_QUEUED = 50;

/**
 * How long a file picker may stay open before we treat the capture as
 * abandoned. Generous, because it covers a user framing a photo, and because
 * the cost of being wrong is one spurious report rather than a lost one.
 */
const CAPTURE_STALE_MS = 2 * 60 * 1000;

// localStorage, not redux-persist (whose store is keyed to the build timestamp
// and is therefore wiped by every deploy) and not sessionStorage (cleared when
// the tab closes, which is exactly the case this exists to catch).
const readStored = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch (e) {
    return null;
  }
};

const writeStored = (key, value) => {
  try {
    window.localStorage.setItem(key, value);
  } catch (e) {
    // Unavailable or full. A diagnostic is not worth surfacing a failure for.
  }
};

const removeStored = (key) => {
  try {
    window.localStorage.removeItem(key);
  } catch (e) {
    // Nothing to clear.
  }
};

const readQueue = () => {
  try {
    const parsed = JSON.parse(readStored(QUEUE_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    // Corrupt or hand-edited. Start over rather than failing.
    return [];
  }
};

const writeQueue = (reports) => {
  try {
    writeStored(QUEUE_KEY, JSON.stringify(reports.slice(-MAX_QUEUED)));
  } catch (e) {
    // Not serialisable, which should not happen for the shapes we build.
  }
};

/**
 * Sends one report. Rejects if it could not be delivered, which is the signal
 * the caller uses to queue it.
 *
 * Note this depends on the very thing that tends to be broken: the request is
 * SigV4-signed, and `getCredentials` throws when the network or the SSO session
 * is gone. So the offline case reliably fails here and is reported later, on a
 * load where credentials work again — that is what the queue is for, and why a
 * report carries `occurredAt` separately from when the server received it.
 */
const send = async (report) => {
  const signedRequest = await createSignedPostRequest({
    apiPath: API_PATH,
    body: report,
  });

  await axios(signedRequest);
};

const buildReport = ({
  stage,
  error,
  file,
  projectId,
  attempt,
  maxAttempts,
  uploadKind,
}) => ({
  stage,
  uploadKind,
  projectId,
  errorName: error?.name,
  errorMessage: error?.message,
  httpStatus: error?.response?.status,
  // The file's shape, never its name or contents: this lands in a log, and a
  // filename is the one field likely to carry something personal.
  fileSizeBytes: file?.size,
  mimeType: file?.type,
  attempt,
  maxAttempts,
  online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
  userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
  appBuild: Config.BUILD_TS,
  occurredAt: new Date().toISOString(),
});

const queueReport = (report) => {
  writeQueue([...readQueue(), report]);
};

/**
 * Records a failed upload. Fire-and-forget: never awaited by callers, never
 * throws, and queues itself for a later attempt if it cannot be delivered now.
 */
export const reportUploadFailure = (details) => {
  try {
    const report = buildReport(details);

    send(report).catch(() => {
      // Could not deliver — almost always the same outage that caused the
      // failure being reported. Keep it for the next load.
      queueReport(report);
    });
  } catch (e) {
    // Building or storing the report failed. Nothing more we can do, and the
    // upload path must not notice.
  }
};

/**
 * Records that a file picker is opening.
 *
 * This is the only thing that can see a capture the app never hears about at
 * all: a mobile browser routinely discards the page while the camera app is in
 * the foreground, and when the user comes back the SPA has reloaded, no change
 * event ever fires, and no handler runs. Nothing throws, so nothing else in
 * this module would ever know a photo was attempted.
 */
export const noteCaptureStarted = (projectId) => {
  try {
    writeStored(
      PENDING_CAPTURE_KEY,
      JSON.stringify({ projectId, startedAt: Date.now() })
    );
  } catch (e) {
    // No marker; we simply lose visibility of this one capture.
  }
};

/** Clears the marker: files arrived, or the user cancelled the picker. */
export const noteCaptureResolved = () => {
  removeStored(PENDING_CAPTURE_KEY);
};

/**
 * Reports a capture marker left behind by a page that died mid-capture.
 *
 * Only counts one older than `CAPTURE_STALE_MS`, so a marker from a picker
 * still legitimately open in another tab is left alone.
 */
const reportAbandonedCapture = () => {
  const raw = readStored(PENDING_CAPTURE_KEY);
  if (!raw) return;

  let pending;
  try {
    pending = JSON.parse(raw);
  } catch (e) {
    removeStored(PENDING_CAPTURE_KEY);
    return;
  }

  const startedAt = pending?.startedAt;
  if (!startedAt || Date.now() - startedAt < CAPTURE_STALE_MS) return;

  removeStored(PENDING_CAPTURE_KEY);

  reportUploadFailure({
    stage: 'capture-abandoned',
    projectId: pending.projectId,
    uploadKind: 'project-camera-button',
  });
};

/**
 * Drains whatever could not be sent earlier, and reports an abandoned capture
 * if one is outstanding. Called once on startup, after the user has loaded, so
 * credentials are warm.
 *
 * Reports are dropped from the queue only once delivered; anything that fails
 * again is written back and retried on the next load.
 */
export const flushUploadDiagnostics = async () => {
  try {
    reportAbandonedCapture();

    const queued = readQueue();
    if (queued.length === 0) return;

    // Clear first so a second flush cannot send the same reports twice; the
    // undelivered ones are written back below.
    writeQueue([]);

    const undelivered = [];

    for (const report of queued) {
      try {
        await send(report);
      } catch (e) {
        undelivered.push(report);
      }
    }

    if (undelivered.length > 0) {
      writeQueue([...readQueue(), ...undelivered]);
    }
  } catch (e) {
    // Startup must not be affected by a diagnostic flush.
  }
};
